/**
 * Money in and out of the revenue grid.
 *
 * **Extracted from the page so it can be tested, because this is the one piece
 * of client code that could silently change a revenue figure.** Everything else
 * on `/revenue` either displays a number or sends it verbatim; these two
 * functions transform it, and revenue is the only data in the app with no
 * upstream to re-derive it from.
 *
 * Two facts they exist to reconcile:
 *
 * · `app.revenue.amount` is `numeric(14,4)`, so the API returns `"561.5000"`.
 *   Money is read at 2dp, and an input pre-filled with four decimal places is an
 *   invitation to mangle a figure while editing it.
 * · Formatting for display introduces thousands separators (`"1,264.50"`), which
 *   are not valid input to the API and are `NaN` to `Number()`.
 *
 * So display formats, input parses, and **the round trip must never alter the
 * value** — which is what `money.test.ts` asserts.
 */

const MONEY = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** `"561.5000"` → `"561.50"`. Non-numeric input is returned untouched, so a
 *  value we do not understand is shown as it arrived rather than blanked. */
export function formatMoney(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return ''
  const parsed = typeof raw === 'number' ? raw : Number(String(raw).replace(/,/g, ''))
  return Number.isFinite(parsed) ? MONEY.format(parsed) : String(raw)
}

/**
 * A grid cell's text to a number, or `null` for **not recorded**.
 *
 * `null` is the whole point: an empty cell records nothing and is never a zero.
 * Verified against the seed workbook — it contains no explicit `0` anywhere, and
 * its interior blanks are a contiguous 27 Jul–9 Aug recording gap across 8–11 of
 * 12 channels while `Arabic`, which starts 10 Aug, is 100% dense.
 */
export function parseMoney(text: string | null | undefined): number | null {
  if (text === null || text === undefined) return null
  const stripped = text.trim().replace(/,/g, '')
  if (stripped === '') return null
  const parsed = Number(stripped)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * A cell's text as the API wants it: a bare decimal string, or `null` to clear.
 *
 * **Never `Number` round-tripped.** `parseFloat` on a figure a client typed can
 * change it — binary floating point is exactly how `229.9` becomes
 * `229.90000000000003`, which is the same class of bug the importer avoids by
 * going through `str()` before `Decimal`. The API parses the string itself.
 */
export function toApiAmount(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null
  const stripped = text.trim().replace(/,/g, '')
  return stripped === '' ? null : stripped
}

/**
 * A keystroke or paste, accepted or rejected — the browser half of §5.1's
 * *"numeric · non-negative"* validation.
 *
 * Returns the value the input should now hold. **Rejects by returning
 * `previous`**, so an invalid character never appears at all rather than being
 * typed and then complained about.
 *
 * The server validates independently and always will — `app/revenue/service.py`
 * plus a `numeric(14,4)` column and a `>= 0` CHECK. **This is convenience, never
 * the control**, exactly as "hiding a nav tab is not access control" applies to
 * the authorization layer.
 *
 * What it allows, and why each rule is here rather than left to the server:
 *
 * · **Empty** — that is *not recorded*, the most important value on the page.
 * · **Digits and at most one `.`** — letters and a second decimal point are the
 *   things a hand actually slips into a numeric grid.
 * · **A trailing `.`** (`"12."`) — a legitimate mid-typing state. Rejecting it
 *   makes the decimal point impossible to type.
 * · **A leading `.`** normalises to `0.`, so `.5` means what the typist meant.
 * · **At most 4 decimals** — the column is `numeric(14,4)`, and silently rounding
 *   a figure a client typed is the one thing revenue must never do, so the fifth
 *   decimal is refused at the keyboard instead.
 * · **No leading `-`** — revenue is a payout. A negative Snap clawback has never
 *   appeared in the sheet and would be a migration, not a typed number.
 * · **Pasted `$`, `,`, spaces and non-breaking spaces are stripped**, because
 *   copying a figure out of the source workbook brings them along.
 */
export function sanitizeMoneyInput(next: string, previous: string): string {
  // Strip what a paste from a spreadsheet legitimately carries.
  let candidate = next.replace(/[$£€\s\u00a0,]/g, '')
  if (candidate === '') return ''
  if (candidate.startsWith('.')) candidate = `0${candidate}`

  // Digits, optionally one dot, optionally up to 4 more digits. A trailing dot
  // is allowed so the point can be typed at all.
  if (!/^\d+(\.\d{0,4})?$/.test(candidate)) return previous
  // Guard the integer side too: numeric(14,4) leaves 10 digits before the point.
  const [whole] = candidate.split('.')
  if ((whole ?? '').length > 10) return previous
  return candidate
}
